import {put, call, all} from 'redux-saga/effects';
import CaseType from "../../entities/caseType";
import * as Actions from "../actions/caseTypeActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";
import {ensureOperationId} from '../../lib/pendingRows';
import {tabSession} from '../../Auth/session';

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type CaseTypeAction = {
    caseTypeId: string;
    caseType: CaseType;
    caseTypes: CaseType[];
    sentAddedOn?: number;
    onQueued?: (saved: boolean) => void;
};

export function* fetchOneCaseType(action: CaseTypeAction) {
	const session = tabSession.captureGeneration();
	try {
		const caseType: CaseType = yield call(api.findOne, 'caseType', action.caseTypeId);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.CASETYPE_FETCH_ONE_SUCCESS, caseTypeId: action.caseTypeId, caseType, sentAddedOn: action.sentAddedOn});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.CASETYPE_FETCH_ONE_FAILURE, caseTypeId: action.caseTypeId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {CaseTypeAction} action
 */
export function* saveCaseType(action: CaseTypeAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ CASETYPE_SAVE saga', action.caseType?.id);

		if (action.caseType === undefined || action.caseType === null) {
			console.error('saveCaseType received a null');
			throw new Error('saveCaseType received a null');
		}

		if (!(action.caseType instanceof CaseType)) {
			action.caseType = new CaseType(action.caseType);
		}
		// OWA-1018: the reducer showed this save at dispatch, stamped after the version this tab holds
		// (OWA-1083), marked pending under this queue key
		const dispatchedAddedOn = action.caseType.addedOn;

		try {
			// Save to IndexedDB
			ensureOperationId(action.caseType);
			const addResult = yield call(idb.add, 'caseType', action.caseType);
			if (addResult.isErr()) {
				console.error(`⮂ saveCaseType: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The save never reached the queue: its row returns to the latest accepted version
				yield put(Actions.revertCaseTypeSave(action.caseType, {sentAddedOn: dispatchedAddedOn}));
				yield put({type: Actions.CASETYPE_SAVE_FAILURE, caseType: action.caseType, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copy replaces the row shown at dispatch
			// (the queue may have stamped it again)
			yield put({type: Actions.CASETYPE_SAVE, caseType: action.caseType, dispatchedAddedOn, queuedAt: performance.now()});

			// A save never deletes autosaved drafts (OWA-958). The editor that wrote a draft
			// removes it when it saves; a draft typed somewhere else may be newer than this copy.
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue);
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: a save that never reached the queue returns its row to the latest accepted version
			if (!queueReported) yield put(Actions.revertCaseTypeSave(action.caseType, {sentAddedOn: dispatchedAddedOn}));
			reportQueued(false);
			yield put({type: Actions.CASETYPE_SAVE_FAILURE, caseType: action.caseType, status: 500, err});

			console.error('Error saving to database', err);
		}
    } finally { reportQueued(false); }
}

/**
 * TODO: Update for saveMany
 * @param {CaseTypeAction} action
 * @param {CaseType[]} action.caseTypes
 */
export function* saveManyCaseTypes(action: CaseTypeAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ CASETYPE_SAVE_MANY saga', action.caseTypes.map(caseType => caseType.id).join(','));

        if (!Array.isArray(action.caseTypes)) {
            console.error('saveManyCaseTypes expected an array, received', action.caseTypes);
            throw new Error('saveManyCaseTypes expected an array');
        }
		// OWA-1018: the reducer showed these saves at dispatch (see saveCaseType), under these queue keys
		const dispatchedAddedOns: number[] = action.caseTypes.map(caseType => caseType?.addedOn);
		const revertAll = () => action.caseTypes.map((caseType, i) =>
			put(Actions.revertCaseTypeSave(caseType, {sentAddedOn: dispatchedAddedOns[i]})));

		try {
			const toSave: CaseType[] = [];

			// Save each item to IndexedDB
			for(let i = 0; i < action.caseTypes.length; i++) {
				const caseType = action.caseTypes[i];
				caseType.persisted = false;
				ensureOperationId(caseType);
				toSave.push(caseType);
			}

			const addResult = yield call(idb.add, 'caseType', toSave);
			if (addResult.isErr()) {
				console.error(`⮂ saveManyCaseTypes: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The saves never reached the queue: their rows return to the latest accepted versions
				yield all(revertAll());
				yield put({type: Actions.CASETYPE_SAVE_MANY_FAILURE, caseTypes: action.caseTypes, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copies replace the rows shown at dispatch
			yield put({type: Actions.CASETYPE_SAVE_MANY, caseTypes: toSave, dispatchedAddedOns, queuedAt: performance.now()});
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue)
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: saves that never reached the queue return their rows to the latest accepted versions
			if (!queueReported) yield all(revertAll());
			reportQueued(false);
			yield put({type: Actions.CASETYPE_SAVE_MANY_FAILURE, caseTypes: action.caseTypes, status: 500, err});

			console.error(`Error saving ${action.caseTypes.length} caseTypes to database`, err);
		}
    } finally { reportQueued(false); }
}

/**
 * @param {CaseTypeAction} action
 */
export function* removeCaseType(action: CaseTypeAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
	if (!userId || action.caseType === undefined || action.caseType === null || !(action.caseType instanceof CaseType)) {
			console.error('removeCaseType expected type CaseType, received', action.caseType);
			reportQueued(false);
			throw new Error('removeCaseType expected type CaseType');
		}

	try {
		// Mark as deleted. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.caseType.status = 'D';
		action.caseType.addedOn = newHRTimestamp();
		action.caseType.addedBy = userId;

		// Save to IndexedDB
		ensureOperationId(action.caseType);
		const addResult = yield call(idb.add, 'caseType', action.caseType);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ removeCaseType: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			yield put({type: Actions.CASETYPE_REMOVE_FAILURE, caseType: action.caseType, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.CASETYPE_REMOVE, caseType: action.caseType});
		session.assertCurrent();

        reportQueued(true);

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.CASETYPE_REMOVE_FAILURE, caseType: action.caseType, status: 500, err});

		console.error('Error deleting from database', err);
	}
    finally { reportQueued(false); }
}

/**
	* Restore the last version of this CaseType.
	*
	* @param {CaseTypeAction} action
	*/
export function* restoreCaseType(action: CaseTypeAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreCaseType expected getCurrentUserId() to return a value');
        return;
    }

	if (action.caseType === undefined || action.caseType === null) {
        console.error('restoreCaseType expected a CaseType, received null');
        throw new Error('restoreCaseType expected a CaseType, received null');
    }
    else if (!(action.caseType instanceof CaseType)) action.caseType = new CaseType(action.caseType); 

	try {
		// Mark as restored. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.caseType.status = 'U';
		action.caseType.addedOn = newHRTimestamp();
		action.caseType.addedBy = userId;
		action.caseType.restore = true;

		// Save to IndexedDB
		ensureOperationId(action.caseType);
		const addResult = yield call(idb.add, 'caseType', action.caseType);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ restoreCaseType: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.CASETYPE_RESTORE_FAILURE, caseType: action.caseType, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.CASETYPE_RESTORE, caseType: action.caseType});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.CASETYPE_RESTORE_FAILURE, caseType: action.caseType, status: 500, err});

		console.error('Error restoring from database', err);
	}
}